<template>
  <div class="body">
    <h2>New England Grand Prix</h2>
    <h3>Motivation</h3>
    <p>
      The New England Grand Prix is a series of tournaments in which players
      accumulate Grand Prix points based on their performance at Grand Prix
      events. At the end of the calendar year, prizes are awarded based on
      overall Grand Prix standings to the top two players in each rating class
      and top two overall. There is no additional cost to the player to
      participate in the Grand Prix; organizers of GP events contribute $5 for
      every entry to the GP prize fund.
    </p>
    <p>
      The primary goal of the New England Grand Prix is to encourage player to
      participate in more events, reward consistency, and discourage early
      withdrawls from tournaments. We are also hoping that this Grand Prix will
      encourage directors to host more tournaments, and increase both the number
      of tournaments in New England as well as the turnout for these events.
    </p>
    <p>
      All events that fulfill the eligibility requirements listed below are
      welcome and encouraged to participate in the New England Grand Prix. If
      you would like your event to be part of the New England Grand Prix, please
      contact Joseph Truelson at josephtruelson@gmail.com.
    </p>
    <p>
      If you would like to sponsor the New England Grand Prix, please contact
      Joseph Truelson. Sponsorship can be directed at a specific prize category
      or the entire prize fund.
    </p>
    <p>
      The rules of this Grand Prix are based on the
      <a href="https://nwchess.com/gp/gp_rules.htm" target="_blank"
        >North-West Chess Grand Prix rules</a
      >.
    </p>

    <h3>Rules</h3>
    <ol type="1">
      <li>Event Eligibility</li>
      <ol type="A">
        <li>
          Events must be advertised as Grand Prix (GP) events on the New England
          Grand Prix website and on US Chess TLA (Tournament Life Announcement)
          online (this can be done for free for US Chess Affiliates) at least 30
          days prior to the start date of the event. A link to the Grand Prix
          website must be included in the description.
        </li>
        <li>Events must have sections available for all players.</li>
        <ol type="a">
          <li>
            Events limited to masters, amateurs, seniors, juniors, women, club
            members, residents of specific geographical areas, etc., cannot be
            considered GP events.
          </li>
        </ol>
        <li>
          Organizers must remit $5 per entrant depending on the prize fund of
          the event to the Grand Prix Administrator, with a minimum of $50 per
          event.
        </li>
        <ol type="a">
          <li>
            Players just used as “fillers” for a single round are not counted,
            nor will they receive GP points.
          </li>
          <li>Players who never start the event will not be counted.</li>
          <li>
            All players who actually participate in the event are counted, even
            those from outside New England.
          </li>
          <li>Payment should be made to the Grand Prix Administrator.</li>
          <li>
            Payment should be made within 5 business days of the end of the
            event. Organizers should contact the Grand Prix Administrator to
            determine the method of payment.
          </li>
          <li>
            Results, or the USCF tournament ID number should be sent by mail or
            email to the Grand Prix administrator.
          </li>
          <li>
            Events for which payment is not received shall not be included in
            the final Grand Prix calculations. Organizers shall be suspended
            from advertising subsequent events as Grand Prix events.
          </li>
        </ol>
        <li>
          Events must be located in New England to be considered for the New
          England Grand Prix.
        </li>
        <li>
          Events must be US Chess Regular Rated. Dual Rated events shall count,
          but not Quick only, Blitz, or Online events.
        </li>
      </ol>
      <li>Player Eligibility</li>
      <ol type="A">
        <li>
          Players eligible for prizes must live in New England (New Hampshire,
          Vermont, Massachusetts, Maine, Connecticut, or Rhode Island).
        </li>
        <ol type="a">
          <li>
            Determination of residence will be taken from the US Chess website,
            unless other information is provided to the administrator.
          </li>
        </ol>
      </ol>
      <li>Prizes</li>
      <ol type="A">
        <li>
          The prize fund will consist of the funds collected from each
          tournament and an amount gained through sponsorship and donations.
          Interest will also be accrued.
        </li>
        <ol type="a">
          <li>Sponsorship is not required for this program to operate.</li>
          <li>
            The sponsor has naming rights for the program, which should
            incorporate the words “New England Grand Prix.”
          </li>
          <li>
            Donations may be given to the overall prize fund or for a specific
            prize (state, age range, male/female, etc.) at the donor's
            direction.
          </li>
        </ol>
        <li>The prize fund shall be divided into thirteen prizes</li>
        <ol type="a">
          <li>
            First place in classes Master, Expert, A, B, C, and D-and-Below will
            receive 10% of the resultant prize fund.
          </li>
          <li>
            Second place in classes Master, Expert, A, B, C, and D-and-Below
            will receive 5% of the resultant prize fund.
          </li>
          <li>
            The player with the highest overall point total will receive 10% of
            the resultant prize fund in addition to the class prize earned (for
            a total of 20% of the prize fund).
          </li>
        </ol>
        <li>
          Ratings for class prizes will be monitored throughout the competition
          year.
        </li>
        <ol type="a">
          <li>
            The highest official rating throughout the year will be used for the
            purpose of classification.
          </li>
          <ol type="i">
            <li>
              A player’s starting rating will be that of the annual (December)
              rating supplement from the previous year.
            </li>
            <li>
              Ratings will be updated twelve times through the year, coinciding
              with the release of US Chess monthly rating supplements.
            </li>
            <li>
              During a participation year, a player may move up one or more
              classes through official ratings changes, but will never move down
              a class until the next program year.
            </li>
          </ol>
        </ol>
        <li>
          Barring unforeseen circumstances, prizes will be distributed between
          January 15th and January 31st of the following year.
        </li>
      </ol>
      <li>Points</li>
      <ol type="A">
        <li>
          Players in a Grand Prix event shall be awarded points equal to their
          scores in the event.
        </li>
        <ol type="a">
          <li>This shall include points for full and half-point byes.</li>
          <li>
            Players used as fillers and not counted for fee payment as above
            will not receive points for any score in the event.
          </li>
        </ol>
        <li>Players who complete their schedule shall receive a bonus.</li>
        <ol type="a">
          <li>Players who withdraw or forfeit will not receive the bonus.</li>
          <li>
            This bonus will be 2 points added to the player's final score,
            before accounting for the event multiplier.
          </li>
        </ol>
        <li>
          Multipliers will be applied to events with guaranteed prize funds
          above a set threshold.
        </li>
        <ol type="a">
          <li>The current multiplier levels are as follows:</li>
          <ol type="i">
            <li>
              Events with guaranteed prize funds of less than $500 will have a
              multiplier of one.
            </li>
            <li>
              Events with guaranteed prize funds of $500 to $999 will have a
              multiplier of two.
            </li>
            <li>
              Events with guaranteed prize funds between $1000 and $1999 will
              have a multiplier of three.
            </li>
            <li>
              Events with guaranteed prize funds between $2000 and $4999 will
              have a multiplier of four.
            </li>
            <li>
              Events with guaranteed prize funds between $5000 and $9999 will
              have a multiplier of five.
            </li>
            <li>
              Events with guaranteed prize funds between $10000 and $19999 will
              have a multiplier of six.
            </li>
            <li>
              Events with guaranteed prize funds of $20000 or more will have a
              multiplier of seven.
            </li>
          </ol>
          <li>
            Multipliers for events with “based-on” prize funds will be
            calculated at half of the advertised level.
          </li>
          <li>
            Multipliers are applied to all the points gained by all players,
            including those who withdrew or forfeited.
          </li>
        </ol>
      </ol>
    </ol>
    <p>Rules last updated by Hayden Collins on 2024/5/16.</p>
  </div>
</template>

<script>
import Tournament from "./Tournament.vue";
export default {
  components: { Tournament },
};
</script>

<style scoped>
li {
  text-align: left;
}

@media only screen and (max-width: 700px) {
  .body {
    width: 90%;
  }
}
</style>
